import type { ViewerArtifact } from "./model";
import { filename, sheetCsv } from "./model";
import { saveBlob } from "./files";
import { htmlDocument } from "./html";

export type ExportFormat = "html" | "pdf" | "docx" | "pptx" | "xlsx" | "csv";
export const exportFormats = (kind: ViewerArtifact["kind"]): ExportFormat[] => kind === "sheet" ? ["xlsx", "csv", "html"] : kind === "presentation" ? ["pptx", "pdf", "html"] : ["docx", "pdf", "html"];

export async function exportArtifact(artifact: ViewerArtifact, title: string, format: ExportFormat, activeSheet = 0): Promise<void> {
  if (!exportFormats(artifact.kind).includes(format)) throw new Error("This export format is not available for this artifact.");
  const name = filename(title, format);
  if (format === "html") return saveBlob(new Blob([htmlDocument(artifact.html, title)], { type: "text/html;charset=utf-8" }), name);
  if (format === "csv") {
    const sheet = artifact.sheets[activeSheet];
    if (!sheet) throw new Error("Choose a worksheet to export.");
    return saveBlob(new Blob(["\uFEFF", sheetCsv(sheet)], { type: "text/csv;charset=utf-8" }), filename(`${title}-${sheet.name}`, format));
  }
  if (format === "xlsx") {
    const { Workbook } = await import("exceljs");
    const book = new Workbook();
    book.calcProperties.fullCalcOnLoad = true;
    for (const sheet of artifact.sheets) {
      const ws = book.addWorksheet(sheet.name);
      sheet.rows.forEach((row, i) => row.forEach((cell, j) => {
        const target = ws.getCell(i + 1, j + 1);
        target.value = cell.formula ? { formula: cell.formula, result: cell.value ?? undefined } : cell.value;
        if (cell.format) target.numFmt = cell.format;
      }));
      ws.columns.forEach(col => { col.width = 22; });
    }
    return saveBlob(new Blob([new Uint8Array(await book.xlsx.writeBuffer())], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), name);
  }
  const doc = new DOMParser().parseFromString(artifact.html, "text/html");
  if (format === "docx") {
    const d = await import("docx");
    const runs = (el: Element): (InstanceType<typeof d.TextRun> | InstanceType<typeof d.ImageRun>)[] => {
      const walk = (node: Node, bold = false, italics = false): (InstanceType<typeof d.TextRun> | InstanceType<typeof d.ImageRun>)[] => {
        if (node.nodeType === Node.TEXT_NODE) return [new d.TextRun({ text: node.textContent || "", bold, italics })];
        if (!(node instanceof Element)) return [];
        if (node.tagName === "IMG") return images.has(node) ? [images.get(node)!] : [];
        if (node.tagName === "BR") return [new d.TextRun({ break: 1 })];
        return Array.from(node.childNodes).flatMap(n => walk(n, bold || /^(B|STRONG)$/.test(node.tagName), italics || /^(I|EM)$/.test(node.tagName)));
      };
      return walk(el);
    };
    const images = new Map<Element, InstanceType<typeof d.ImageRun>>();
    for (const image of Array.from(doc.querySelectorAll("img"))) {
      const source = new Image(); source.src = image.src; await source.decode();
      const canvas = document.createElement("canvas"); canvas.width = source.naturalWidth; canvas.height = source.naturalHeight;
      canvas.getContext("2d")!.drawImage(source, 0, 0);
      const data = Uint8Array.from(atob(canvas.toDataURL("image/png").split(",")[1]), c => c.charCodeAt(0));
      const scale = Math.min(1, 720 / source.naturalWidth, 360 / source.naturalHeight);
      images.set(image, new d.ImageRun({ type: "png", data, transformation: { width: source.naturalWidth * scale, height: source.naturalHeight * scale } }));
    }
    const orderedLists: Element[] = Array.from(doc.querySelectorAll("ol"));
    const blocks = (root: Element): (InstanceType<typeof d.Paragraph> | InstanceType<typeof d.Table>)[] => Array.from(root.children).flatMap(el => {
      if (el.tagName === "IMG") return [new d.Paragraph({ children: [images.get(el)!] })];
      if (el.tagName === "TABLE") {
        const rows = Array.from(el.querySelectorAll("tr")).map(tr => new d.TableRow({
          children: Array.from(tr.children).map(td => new d.TableCell({ children: [new d.Paragraph({ children: runs(td) })] })),
        }));
        return [new d.Table({ width: { size: 100, type: d.WidthType.PERCENTAGE }, rows })];
      }
      if (["SECTION", "ARTICLE", "DIV", "UL", "OL"].includes(el.tagName)) return blocks(el);
      const headings = { H1: d.HeadingLevel.HEADING_1, H2: d.HeadingLevel.HEADING_2, H3: d.HeadingLevel.HEADING_3 };
      return [new d.Paragraph({ children: runs(el), heading: headings[el.tagName as keyof typeof headings], bullet: el.tagName === "LI" && el.parentElement?.tagName !== "OL" ? { level: 0 } : undefined, numbering: el.tagName === "LI" && el.parentElement?.tagName === "OL" ? { reference: `ordered-${orderedLists.indexOf(el.parentElement)}`, level: 0 } : undefined, spacing: { after: 200 } })];
    });
    const children = blocks(doc.body);
    if (!children.length) children.push(new d.Paragraph(doc.body.textContent || ""));
    return saveBlob(await d.Packer.toBlob(new d.Document({
      numbering: { config: orderedLists.map((_, i) => ({ reference: `ordered-${i}`, levels: [{ level: 0, format: d.LevelFormat.DECIMAL, text: "%1.", alignment: d.AlignmentType.LEFT }] })) },
      styles: { default: { document: { run: { font: "Arial", size: 24 }, paragraph: { spacing: { line: 372 } } } }, paragraphStyles: [
        { id: "Heading1", name: "Heading 1", basedOn: "Normal", next: "Normal", run: { size: 48, bold: true }, paragraph: { spacing: { before: 0, after: 360 } } },
        { id: "Heading2", name: "Heading 2", basedOn: "Normal", next: "Normal", run: { size: 36, bold: true }, paragraph: { spacing: { before: 420, after: 180 } } },
      ] }, sections: [{ properties: { page: { size: { width: 12240, height: 15840 }, margin: { top: 720, bottom: 720, left: 720, right: 720 } } }, children }],
    })), name);
  }
  if (format === "pptx") {
    const { default: PptxGenJS } = await import("pptxgenjs");
    const deck = new PptxGenJS();
    deck.layout = "LAYOUT_WIDE"; deck.title = title; deck.author = "Definable";
    deck.theme = { headFontFace: "Arial", bodyFontFace: "Arial" };
    await withArtifactFrame(artifact, title, async body => {
      for (const section of Array.from(body.querySelectorAll<HTMLElement>("[data-slide]"))) {
        const slide = deck.addSlide();
        const origin = section.getBoundingClientRect();
        for (const el of Array.from(section.querySelectorAll<HTMLElement>("h1,h2,h3,h4,p,li,table,img,blockquote,pre"))) {
          if (el.parentElement?.closest("table,p,li,blockquote,pre")) continue;
          const rect = el.getBoundingClientRect();
          const css = body.ownerDocument.defaultView!.getComputedStyle(el);
          const position = { x: (rect.left - origin.left) / 72, y: (rect.top - origin.top) / 72, w: rect.width / 72, h: rect.height / 72 };
          if (rect.bottom > origin.bottom + 1) throw new Error("A slide has overflowing content. Split it across more slides before exporting.");
          if (el.tagName === "TABLE") {
            const rows = Array.from(el.querySelectorAll("tr")).map(tr => Array.from(tr.children).map(td => ({ text: td.textContent || "", options: { bold: td.tagName === "TH", fill: { color: td.tagName === "TH" ? "F4F4F2" : "FFFFFF" } } })));
            slide.addTable(rows, { ...position, fontSize: 16, border: { pt: 0.75, color: "D8D8D8" }, margin: 0.1, autoPage: false });
          } else if (el.tagName === "IMG") {
            const img = el as HTMLImageElement;
            slide.addImage({ data: img.src, ...position });
          } else {
            const runs: { text: string; options: { bold?: boolean; italic?: boolean; underline?: { style: "sng" } } }[] = [];
            const textNodes = body.ownerDocument.createTreeWalker(el, NodeFilter.SHOW_TEXT);
            while (textNodes.nextNode()) {
              const parentStyle = body.ownerDocument.defaultView!.getComputedStyle(textNodes.currentNode.parentElement!);
              runs.push({ text: textNodes.currentNode.textContent || "", options: { bold: Number(parentStyle.fontWeight) >= 600, italic: parentStyle.fontStyle === "italic", underline: parentStyle.textDecorationLine.includes("underline") ? { style: "sng" } : undefined } });
            }
            const ordered = el.tagName === "LI" && el.parentElement?.tagName === "OL";
            if (ordered) runs.unshift({ text: `${Array.from(el.parentElement!.children).indexOf(el) + 1}. `, options: {} });
            slide.addText(runs, { ...position, fontSize: parseFloat(css.fontSize), bold: Number(css.fontWeight) >= 600, bullet: el.tagName === "LI" && !ordered ? { indent: 12 } : undefined, margin: 0, color: "202020", valign: "top", breakLine: false });
          }
        }
      }
    });
    const { default: JSZip } = await import("jszip");
    const zip = await JSZip.loadAsync(await deck.write({ outputType: "arraybuffer" }) as ArrayBuffer);
    // PptxGenJS declares a master per slide but writes only the actual masters.
    const types = new DOMParser().parseFromString(await zip.file("[Content_Types].xml")!.async("string"), "application/xml");
    for (const entry of Array.from(types.getElementsByTagName("Override"))) {
      const part = entry.getAttribute("PartName")!.replace(/^\//, "");
      if (part.startsWith("ppt/slideMasters/") && !zip.file(part)) entry.remove();
    }
    zip.file("[Content_Types].xml", new XMLSerializer().serializeToString(types));
    return saveBlob(await zip.generateAsync({ type: "blob", mimeType: "application/vnd.openxmlformats-officedocument.presentationml.presentation" }), name);
  }
  await exportPdf(artifact, title, name);
}

async function exportPdf(artifact: ViewerArtifact, title: string, name: string) {
  const [{ jsPDF }, { default: html2canvas }] = await Promise.all([import("jspdf"), import("html2canvas")]);
  await withArtifactFrame(artifact, title, async body => {
    const slides = artifact.kind === "presentation";
    const pdf = new jsPDF({ orientation: slides ? "landscape" : "portrait", unit: "px", format: slides ? [960, 540] : "a4", hotfixes: ["px_scaling"] });
    const nodes = slides ? Array.from(body.querySelectorAll<HTMLElement>("[data-slide]")) : [body];
    let first = true;
    for (const node of nodes) {
      const canvas = await html2canvas(node, { scale: 1.5, backgroundColor: "#ffffff", logging: false });
      const width = pdf.internal.pageSize.getWidth(), height = pdf.internal.pageSize.getHeight();
      const sliceHeight = Math.floor(height * canvas.width / width);
      const bounds = node.getBoundingClientRect();
      const ratio = canvas.width / bounds.width;
      const lines: { top: number; bottom: number }[] = [];
      const walker = node.ownerDocument.createTreeWalker(node, NodeFilter.SHOW_TEXT);
      while (walker.nextNode()) {
        const range = node.ownerDocument.createRange(); range.selectNodeContents(walker.currentNode);
        for (const rect of Array.from(range.getClientRects())) lines.push({ top: (rect.top - bounds.top) * ratio, bottom: (rect.bottom - bounds.top) * ratio });
      }
      node.querySelectorAll("img,tr").forEach(el => { const r = el.getBoundingClientRect(); lines.push({ top: (r.top - bounds.top) * ratio, bottom: (r.bottom - bounds.top) * ratio }); });
      for (let top = 0; top < canvas.height;) {
        let end = Math.min(canvas.height, top + sliceHeight);
        if (!slides) {
          const crossing = lines.filter(line => line.top < end && line.bottom > end && line.top > top + 40);
          if (crossing.length) end = Math.floor(Math.min(...crossing.map(line => line.top)));
        }
        if (!first) pdf.addPage();
        first = false;
        const slice = document.createElement("canvas"); slice.width = canvas.width; slice.height = end - top;
        slice.getContext("2d")!.drawImage(canvas, 0, top, canvas.width, slice.height, 0, 0, canvas.width, slice.height);
        pdf.addImage(slice.toDataURL("image/png"), "PNG", 0, 0, width, slice.height * width / canvas.width);
        top = end;
      }
    }
    saveBlob(pdf.output("blob"), name);
  });
}

async function withArtifactFrame(artifact: ViewerArtifact, title: string, action: (body: HTMLElement) => Promise<void>) {
  const frame = document.createElement("iframe");
  frame.setAttribute("sandbox", "allow-same-origin");
  frame.title = "Preparing export";
  frame.style.cssText = `position:fixed;left:-10000px;top:0;width:${artifact.kind === "presentation" ? 960 : 816}px;height:1056px;border:0;`;
  let timer: ReturnType<typeof setTimeout>;
  const loaded = new Promise<void>((resolve, reject) => {
    timer = setTimeout(() => reject(new Error("Export preparation timed out. Please retry.")), 15000);
    frame.onload = () => { clearTimeout(timer); resolve(); };
  });
  frame.srcdoc = htmlDocument(artifact.html, title);
  document.body.append(frame);
  try {
    await loaded;
    const body = frame.contentDocument!.body;
    await frame.contentDocument!.fonts.ready;
    await Promise.all(Array.from(body.querySelectorAll("img")).map(img => img.decode()));
    if (artifact.kind === "presentation") {
      for (const slide of Array.from(body.querySelectorAll<HTMLElement>("[data-slide]"))) {
        if (slide.scrollHeight > slide.clientHeight || slide.scrollWidth > slide.clientWidth) throw new Error("A slide has overflowing content. Split it across more slides before exporting.");
      }
    }
    await action(body);
  } finally { clearTimeout(timer!); frame.remove(); }
}
